import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppBar from "../../common/AppBar";
import Loader from "../../common/Loader";
import Snackbar from "../../common/Snackbar";
import {
  createAppSetup,
  getAllAppSetups,
  getAppSetupById,
  updateAppSetup,
} from "../../redux/appSetupSlice";
import { TextField, TextArea, CheckboxField } from "../../common/FormFields";
import { Button } from "../../common/Buttons";
import SignatureAuthSection from "./components/SignatureAuthSection";
import LogoUpload from "./components/LogoUpload";
import SaveModal from "./components/SaveModal";
import CustomSelect from "./components/CustomSelect";
import "./ApplicationSetup.css";
import { buildPermissionContext } from "../../util/permissions";
import { getAuditTrailData, getFieldsIncluded } from "../../util/getAuditTrailData";
import { BASE, BASE_URL } from "../../config";
const ApplicationSetup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { current, loading, items } = useSelector((s) => s.appSetup || {});
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  // Sort items by updated_at (or created_at) in descending order to get the latest
  const lastItem = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) return null;

    // Sort by updated_at first, then created_at if updated_at is not available
    const sortedItems = [...items].sort((a, b) => {
      // Prefer updated_at, fallback to created_at
      const dateA = new Date(a?.updated_at || a?.created_at || 0).getTime();
      const dateB = new Date(b?.updated_at || b?.created_at || 0).getTime();

      // If dates are equal, maintain stable sort (optional)
      if (dateB === dateA) {
        return 0;
      }

      return dateB - dateA; // Descending order (latest first)
    });

    // Debug: Log sorted items to verify sorting is working
    if (sortedItems.length > 0) {
      
    }

    return sortedItems[0]; // Return the latest item
  }, [items]);
  // Track the last fetched ID to prevent infinite loops
  const lastFetchedIdRef = useRef(null);

  useEffect(() => {
    dispatch(getAllAppSetups());
  }, [dispatch]);

  useEffect(() => {
    // Always ensure we have the latest item loaded
    if (lastItem) {
      const id =
        lastItem.application_setup_id ||
        lastItem.id ||
        lastItem.uuid ||
        lastItem._id;

      // Only fetch if current doesn't match the latest item
      const currentId =
        current?.application_setup_id ||
        current?.id ||
        current?.uuid ||
        current?._id;

      // Fetch if no current or if current ID doesn't match latest item ID
      // Also check if we haven't already fetched this ID to prevent infinite loops
      if (id && currentId !== id && lastFetchedIdRef.current !== id) {
        lastFetchedIdRef.current = id;
        dispatch(getAppSetupById(id));
      } else if (id && currentId === id) {
        // Update ref when current matches the latest item
        lastFetchedIdRef.current = id;
      }
    }
    // Remove lastItem from dependencies since it's derived from items
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, current, dispatch]);
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [footerText, setFooterText] = useState("");
  const [companyImageBase64, setCompanyImageBase64] = useState("");
  const [companyImageFile, setCompanyImageFile] = useState(null);
  const [companyImageUrl, setCompanyImageUrl] = useState("");
  const [signatureMethods, setSignatureMethods] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saveComment, setSaveComment] = useState("");
  const [savePassword, setSavePassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Get signature auth methods from localStorage (for audit trail)
  const [authMethods, setAuthMethods] = useState([]);
  useEffect(() => {
    try {
      const storedMethods = localStorage.getItem("signature_auth_methods");
      if (storedMethods) {
        const methods = JSON.parse(storedMethods);
        setAuthMethods(Array.isArray(methods) ? methods : []);
      } else {
        const userInfo = localStorage.getItem("loggedinUserInfo");
        if (userInfo) {
          try {
            const parsed = JSON.parse(userInfo);
            if (
              parsed.signature_auth_methods &&
              Array.isArray(parsed.signature_auth_methods)
            ) {
              setAuthMethods(parsed.signature_auth_methods);
            } else {
              setAuthMethods(["password", "signature"]);
            }
          } catch (e) {
            setAuthMethods(["password", "signature"]);
          }
        } else {
          setAuthMethods(["password", "signature"]);
        }
      }
    } catch (error) {
      console.error("Error parsing signature_auth_methods:", error);
      setAuthMethods(["password", "signature"]);
    }
  }, []);
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(false);
  const [backupTime, setBackupTime] = useState("10:10 AM");
  const [backupHour, setBackupHour] = useState("10");
  const [backupMinute, setBackupMinute] = useState("10");
  const [backupPeriod, setBackupPeriod] = useState("AM");
  const [timezone, setTimezone] = useState("EST");
  const [isEditMode, setIsEditMode] = useState(false);

  // Debug: Log computed styles for disabled checkboxes
  useEffect(() => {
    if (!isEditMode) {
      setTimeout(() => {
        const checkboxes = document.querySelectorAll(
          ".app-setup-container input[type='checkbox']:disabled"
        );
        checkboxes.forEach((checkbox, index) => {
          const computed = window.getComputedStyle(checkbox);
          // Check parent elements for opacity
          let parent = checkbox.parentElement;
          const parentOpacities = [];
          while (parent && parent !== document.body) {
            const parentComputed = window.getComputedStyle(parent);
            if (parentComputed.opacity !== "1") {
              parentOpacities.push({
                element: parent,
                className: parent.className,
                opacity: parentComputed.opacity,
              });
            }
            parent = parent.parentElement;
          }
        });
      }, 500);
    }
  }, [isEditMode]);
  const [backupRetentionPolicy, setBackupRetentionPolicy] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });
  const previousValueRef = useRef("");
  const phoneInputRef = useRef(null);
  const previousCursorPositionRef = useRef(0);
  const previewUrl = useMemo(() => {
    // Priority: base64 (for new uploads) > URL state > existing URL from lastItem/current
    if (companyImageBase64) return companyImageBase64;
    if (companyImageUrl) return companyImageUrl;

    const rawPath =
      lastItem?.company_image ||
      current?.company_logo_url ||
      current?.company_logo ||
      current?.company_image_url ||
      current?.company_image ||
      "";
    const serverUrl = rawPath?.startsWith("http")
      ? rawPath
      : rawPath
      ? `${BASE}${rawPath}`
      : "";
    return serverUrl;
  }, [companyImageBase64, companyImageUrl, current, lastItem]);

  const isEditing = useMemo(() => !!lastItem?.application_setup_id, [lastItem]);

  const populateForm = (item) => {
    if (!item) return;

    // Debug: Log which item is being used
    const itemId =
      item.application_setup_id ||
      item.id ||
      item.uuid ||
      item._id;
    const latestCompanyName = item.company_name || "";
    const latestCompanyAddress = item.company_address || "";
    const latestCompanyPhone = item.company_phone || "";
    const latestFooter = item.footer || "";
    const latestMethods = Array.isArray(item.signature_auth_methods)
      ? item.signature_auth_methods
      : [];
    const latestAutoBackup =
      item.is_autobackup_enabled || item.auto_backup_enabled || false;
    const latestBackupTime =
      item.auto_backup_time || item.backup_time || "10:10 AM";
    const latestTimezone =
      item.application_time_zone || item.timezone || "EST";
    const latestBackupRetentionPolicy =
      item.backup_retention_policy || null;

    setCompanyName(latestCompanyName);
    setCompanyAddress(latestCompanyAddress);
    setCompanyPhone(latestCompanyPhone);
    setFooterText(latestFooter);
    setSignatureMethods(latestMethods);
    setAutoBackupEnabled(latestAutoBackup);
    setBackupTime(latestBackupTime);
    setTimezone(latestTimezone);
    setBackupRetentionPolicy(latestBackupRetentionPolicy);

    // Parse backup time into hour, minute, and period
    const parseBackupTime = (timeString) => {
      if (!timeString) return { hour: "10", minute: "10", period: "AM" };
      const timeMatch = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (timeMatch) {
        return {
          hour: timeMatch[1].padStart(2, "0"),
          minute: timeMatch[2],
          period: timeMatch[3].toUpperCase(),
        };
      }
      // Try 24-hour format
      const time24Match = timeString.match(/(\d{1,2}):(\d{2})/);
      if (time24Match) {
        const hour24 = parseInt(time24Match[1], 10);
        const hour12 = hour24 % 12 || 12;
        const period = hour24 >= 12 ? "PM" : "AM";
        return {
          hour: hour12.toString().padStart(2, "0"),
          minute: time24Match[2],
          period: period,
        };
      }
      return { hour: "10", minute: "10", period: "AM" };
    };

    const parsedTime = parseBackupTime(latestBackupTime);
    setBackupHour(parsedTime.hour);
    setBackupMinute(parsedTime.minute);
    setBackupPeriod(parsedTime.period);

    // Handle existing logo - check for URL first, then base64
    const existingImageUrl =
      item?.company_image ||
      item?.company_logo_url ||
      item?.company_logo ||
      item?.company_image_url ||
      "";

    if (existingImageUrl && !existingImageUrl.includes("base64")) {
      // It's a URL
      const fullUrl = existingImageUrl.startsWith("http")
        ? existingImageUrl
        : `${BASE}${existingImageUrl}`;
      setCompanyImageUrl(fullUrl);
      setCompanyImageBase64(""); // Clear base64
      setCompanyImageFile(null); // Clear file
    } else if (existingImageUrl && existingImageUrl.includes("base64")) {
      // It's base64 (legacy)
      setCompanyImageBase64(existingImageUrl);
      setCompanyImageUrl(""); // Clear URL
      setCompanyImageFile(null); // Clear file
    } else {
      setCompanyImageBase64("");
      setCompanyImageUrl("");
      setCompanyImageFile(null);
    }
  };

  // Strictly autofill from the last item (latest by updated_at/created_at)
  useEffect(() => {
    if (lastItem) {
      populateForm(lastItem);
    }
  }, [lastItem]);

  // Handle logo upload - store both File object and base64 for preview
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Store the File object for S3 upload
      setCompanyImageFile(file);
      // Clear existing URL since we're uploading a new file
      setCompanyImageUrl("");

      // Also store base64 for preview
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          setCompanyImageBase64(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
    // allow same file re-select
    event.target.value = "";
  };

  // Validate US phone number format (must have exactly 10 digits)
  const isValidUSPhoneNumber = (phone) => {
    if (!phone) return false;
    // Remove all non-numeric characters
    const digits = phone.replace(/\D/g, "");
    // Must have exactly 10 digits
    return digits.length === 10;
  };

  // Format phone number as XXX-XXX-XXXX
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const numbersOnly = value.replace(/\D/g, "").slice(0, 10);

    // Format as XXX-XXX-XXXX
    if (numbersOnly.length === 0) {
      return "";
    } else if (numbersOnly.length <= 3) {
      return numbersOnly;
    } else if (numbersOnly.length <= 6) {
      return `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3)}`;
    } else {
      return `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(
        3,
        6
      )}-${numbersOnly.slice(6)}`;
    }
  };

  // Handle phone input - format as XXX-XXX-XXXX while typing
  const handlePhoneChange = (e) => {
    const input = e.target;
    const value = input.value;
    const cursorPosition = input.selectionStart || 0;

    // Get the old formatted value
    const oldFormattedValue = companyPhone || "";
    const oldDigits = oldFormattedValue.replace(/\D/g, "");

    // Extract digits from new value
    const newDigits = value.replace(/\D/g, "");

    // Check if we're adding digits (not just deleting)
    const isAdding = newDigits.length > oldDigits.length;

    // If we already have 10 digits and user is trying to add more, prevent it
    if (oldDigits.length >= 10 && isAdding) {
      // Calculate cursor position in old value based on where user was trying to type
      // Count digits before cursor in the new value (includes the attempted new digit)
      const digitsBeforeCursorInNew = (
        value.substring(0, cursorPosition).match(/\d/g) || []
      ).length;
      // The cursor position should be at this digit index in the old value
      // (since we're blocking the new digit, we use the same digit count)
      const targetDigitIndex = Math.min(
        digitsBeforeCursorInNew - 1,
        oldDigits.length - 1
      );

      // Find the position in old formatted value for this digit index
      let restoreCursorPosition = oldFormattedValue.length;
      let digitCount = 0;
      for (let i = 0; i < oldFormattedValue.length; i++) {
        if (/\d/.test(oldFormattedValue[i])) {
          if (digitCount === targetDigitIndex) {
            restoreCursorPosition = i + 1;
            if (oldFormattedValue[i + 1] === "-") {
              restoreCursorPosition = i + 2;
            }
            break;
          }
          digitCount++;
        }
      }

      // Revert to old value - don't allow adding more digits
      setCompanyPhone(oldFormattedValue);
      previousCursorPositionRef.current = restoreCursorPosition;

      setTimeout(() => {
        if (phoneInputRef.current) {
          phoneInputRef.current.setSelectionRange(
            restoreCursorPosition,
            restoreCursorPosition
          );
        }
      }, 0);
      return;
    }

    // Count how many digits are before the cursor
    const digitsBeforeCursor = (
      value.substring(0, cursorPosition).match(/\d/g) || []
    ).length;

    // Format the phone number
    const formatted = formatPhoneNumber(value);

    // Calculate new cursor position in the formatted string
    let newCursorPosition = formatted.length;
    let digitCount = 0;

    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        digitCount++;
        if (digitCount === digitsBeforeCursor) {
          // Place cursor after this digit
          newCursorPosition = i + 1;
          // If there's a hyphen after, place cursor after the hyphen
          if (formatted[i + 1] === "-") {
            newCursorPosition = i + 2;
          }
          break;
        }
      }
    }

    // Store cursor position for next time
    previousCursorPositionRef.current = newCursorPosition;

    // Update the state
    setCompanyPhone(formatted);

    // Restore cursor position after React updates the DOM
    setTimeout(() => {
      if (phoneInputRef.current) {
        const finalPosition = Math.min(
          Math.max(0, newCursorPosition),
          formatted.length
        );
        phoneInputRef.current.setSelectionRange(finalPosition, finalPosition);
      }
    }, 0);
  };

  // Handle backspace to delete digit before hyphen when cursor is at hyphen
  const handlePhoneKeyDown = (e) => {
    if (e.key === "Backspace") {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart || 0;

      // If cursor is at a hyphen, delete the digit before it
      if (value[cursorPosition - 1] === "-") {
        e.preventDefault();

        // Find the digit before the hyphen
        let digitIndex = -1;
        let digitCount = 0;
        for (let i = 0; i < cursorPosition - 1; i++) {
          if (/\d/.test(value[i])) {
            digitCount++;
            digitIndex = i;
          }
        }

        if (digitIndex >= 0) {
          // Remove the digit before the hyphen
          const digits = value.replace(/\D/g, "");
          const digitToRemove = digits[digitCount - 1];
          const newDigits =
            digits.substring(0, digitCount - 1) + digits.substring(digitCount);
          const formatted = formatPhoneNumber(newDigits);

          // Calculate new cursor position
          let newCursorPosition = formatted.length;
          let count = 0;
          for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i])) {
              count++;
              if (count === digitCount - 1) {
                newCursorPosition = i + 1;
                if (formatted[i + 1] === "-") {
                  newCursorPosition = i + 2;
                }
                break;
              }
            }
          }

          setCompanyPhone(formatted);
          previousCursorPositionRef.current = newCursorPosition;

          setTimeout(() => {
            if (phoneInputRef.current) {
              phoneInputRef.current.setSelectionRange(
                newCursorPosition,
                newCursorPosition
              );
            }
          }, 0);
        }
        return;
      }
    }
  };

  // Prevent non-numeric input on keypress
  const handlePhoneKeyPress = (e) => {
    // Allow: numbers, navigation keys, and control keys
    const isNumber = e.key >= "0" && e.key <= "9";
    const isControlKey =
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "Tab" ||
      e.key === "Escape" ||
      e.key === "Enter" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "Home" ||
      e.key === "End" ||
      e.ctrlKey ||
      e.metaKey;

    // If trying to type a number and we already have 10 digits, prevent it
    if (isNumber) {
      const currentDigits = (companyPhone || "").replace(/\D/g, "");
      if (currentDigits.length >= 10) {
        e.preventDefault();
        return;
      }
    }

    if (isNumber || isControlKey) {
      return; // Allow the default behavior
    }
    // Prevent all other keys (like letters, symbols, etc.)
    e.preventDefault();
  };

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", type: "" });
  };

  // Upload to S3 helper function
  const uploadToS3 = async (data, fileName, fileType) => {
    try {
      // Get authorization token from localStorage or other storage
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("auth_token") ||
        sessionStorage.getItem("token") ||
        "";

      if (!token) {
        throw new Error("No authorization token found");
      }

      // 1. Get presigned URL from backend
      const response = await fetch(
        `${BASE_URL}upload/get-upload-urls`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            folder: "application-setup",
            files: [{ fileName, fileType }],
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get upload URL: ${response.status}`);
      }

      const responseData = await response.json();
      const { uploadUrl, fileUrl } = responseData.files[0];

      // 2. Convert to blob if needed
      let blob;

      if (data instanceof Blob || data instanceof File) {
        blob = data;
      } else if (typeof data === "string") {
        if (!data.startsWith("data:")) {
          throw new Error("Invalid data format");
        }

        try {
          const base64Response = await fetch(data);
          if (!base64Response.ok) {
            throw new Error(
              `Failed to convert base64 to blob: ${base64Response.status}`
            );
          }
          blob = await base64Response.blob();
        } catch (blobError) {
          throw new Error(`Base64 conversion failed: ${blobError.message}`);
        }
      } else {
        throw new Error("Invalid data type provided");
      }

      // Validate blob size
      if (blob.size === 0) {
        throw new Error("Generated blob is empty");
      }

      const sizeInMB = blob.size / (1024 * 1024);
      if (sizeInMB > 100) {
        throw new Error(`File too large: ${sizeInMB.toFixed(2)}MB (max 100MB)`);
      }

      // 3. Upload to S3 using presigned URL with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes

      try {
        const s3Response = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": fileType,
          },
          body: blob,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!s3Response.ok) {
          throw new Error(`S3 upload failed: ${s3Response.status}`);
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === "AbortError") {
          throw new Error("S3 upload timed out after 5 minutes");
        }
        throw fetchError;
      }

      return fileUrl;
    } catch (error) {
      throw error;
    }
  };

  const validateFields = () => {
    if (!companyName || companyName.trim() === "") {
      showSnackbar("Company Name is mandatory.", "error");
      return false;
    }

    if (!companyAddress || companyAddress.trim() === "") {
      showSnackbar("Company Address is mandatory.", "error");
      return false;
    }

    if (!companyPhone || companyPhone.trim() === "") {
      showSnackbar("Company Phone is mandatory.", "error");
      return false;
    }

    if (!isValidUSPhoneNumber(companyPhone)) {
      showSnackbar(
        "Company Phone must be a valid US phone number (10 digits). Format: XXX-XXX-XXXX",
        "error"
      );
      return false;
    }

    if (!previewUrl || previewUrl.trim() === "") {
      showSnackbar("Company Logo is mandatory.", "error");
      return false;
    }

    if (!footerText || footerText.trim() === "") {
      showSnackbar("Footer is mandatory.", "error");
      return false;
    }

    if (!signatureMethods || signatureMethods.length === 0) {
      showSnackbar(
        "At least one Signature Authentication Method is mandatory.",
        "error"
      );
      return false;
    }


    return true;
  };

  const handleSave = () => {
    if (!validateFields()) {
      return;
    }
    setShowModal(true);
  };

  const title = isEditMode ? "Edit Application Setup" : "Application Setup"
  const handleConfirmSave = async (signature) => {
    setIsLoading(true);

    try {
      let logoUrl = companyImageUrl;

      // Upload logo to S3 if a new file was uploaded
      if (companyImageFile) {
        try {
          const timestamp = new Date().getTime();
          const fileExtension = companyImageFile.name.split(".").pop() || "png";
          const fileName = `company-logo-${timestamp}.${fileExtension}`;
          const fileType = companyImageFile.type || "image/png";

          logoUrl = await uploadToS3(companyImageFile, fileName, fileType);

        } catch (uploadError) {
          console.error("❌ Error uploading company logo to S3:", uploadError);
          showSnackbar(
            "Error uploading company logo: " +
              (uploadError?.message || "Unknown error"),
            "error"
          );
          setIsLoading(false);
          return;
        }
      } else if (!logoUrl) {
        // If no new file and no existing URL, check if we have an existing logo URL
        const existingLogoUrl =
          lastItem?.company_image ||
          current?.company_logo_url ||
          current?.company_logo ||
          current?.company_image_url ||
          current?.company_image ||
          "";

        if (existingLogoUrl) {
          logoUrl = existingLogoUrl.startsWith("http")
            ? existingLogoUrl
            : `${BASE_URL}${existingLogoUrl}`;
        }
      }

      const payload = {
        signature_auth_methods: signatureMethods,
        company_name: companyName,
        company_address: companyAddress,
        company_phone: companyPhone || undefined,
        footer: footerText,
        company_image_url: logoUrl || undefined,
        company_image_base64: undefined, // Don't send base64 anymore
        password: savePassword || undefined,
        is_autobackup_enabled: autoBackupEnabled,
        auto_backup_time: (() => {
          // Handle empty values - default to 00:00 AM (midnight)
          const hour =
            backupHour === "" || isNaN(parseInt(backupHour, 10))
              ? "00"
              : backupHour;
          const minute =
            backupMinute === "" || isNaN(parseInt(backupMinute, 10))
              ? "00"
              : backupMinute;
          const period = backupPeriod || "AM";

          // Convert 12-hour format to 24-hour format
          let hour24 = parseInt(hour, 10);
          if (isNaN(hour24)) {
            hour24 = 0;
          }

          if (period === "PM" && hour24 !== 12) {
            hour24 += 12;
          } else if (period === "AM" && hour24 === 12) {
            hour24 = 0;
          }

          return `${hour24.toString().padStart(2, "0")}:${minute.padStart(
            2,
            "0"
          )}:00`;
        })(),
        application_time_zone: timezone,
        backup_retention_policy: {
          daily: 10,
          weekly: 5,
          monthly: 13,
          yearly: 3,
        },
      };

      // Generate audit trail data similar to Login.js
      // Use authMethods from localStorage (not signatureMethods from checkbox)
      const audit_trail_data = await getAuditTrailData({
        signature: authMethods.includes("signature") ? signature : "",
        ...(authMethods.includes("password") && savePassword
          ? { hash: savePassword }
          : {}),
        ...(!authMethods.includes("password")
          ? {
              is_password_required: false,
            }
          : {
              is_password_required: true,
            }),
        action: isEditing
          ? "UPDATE_APPLICATION_SETUP"
          : "CREATE_APPLICATION_SETUP",
        comment: saveComment || "Application Setup Save",
        version_content: {
          signature: signature,
          action_to_show: isEditing
            ? "Updated Application Setup"
            : "Created Application Setup",
          formData:{
            action_heading: title,
            company_name: payload.company_name,
            company_address: payload.company_address,
            company_phone: payload.company_phone,
            footer: payload.footer,
            company_image_url: payload.company_image_url,
            signature_auth_methods: payload.signature_auth_methods,
            is_autobackup_enabled: payload.is_autobackup_enabled,
            auto_backup_time: payload.auto_backup_time,
            application_time_zone: payload.application_time_zone,
          },
          confirmationPopupInfo:{
            savedBy:localStorage.getItem("user_name") || "User",
            password_bullets:savePassword?.length,
            ...getFieldsIncluded()
          }
        },
        password: savePassword || undefined,
      },false);

      payload.audit_trail_data = audit_trail_data;
      const action = isEditing
        ? updateAppSetup({
            id: lastItem?.application_setup_id,
            data: payload,
          })
        : createAppSetup(payload);

      await dispatch(action).unwrap();

      // Update auth methods in localStorage after successful save
      // if (signatureMethods && signatureMethods.length > 0) {
      //   // Store in separate key for backward compatibility
      //   localStorage.setItem(
      //     "signature_auth_methods",
      //     JSON.stringify(signatureMethods)
      //   );

      //   // Also update in loggedinUserInfo if it exists
      //   const loggedinUserInfo = localStorage.getItem("loggedinUserInfo");
      //   if (loggedinUserInfo) {
      //     try {
      //       const userInfo = JSON.parse(loggedinUserInfo);
      //       userInfo.signature_auth_methods = signatureMethods;
      //       localStorage.loggedinUserInfo = JSON.stringify(userInfo);
      //     } catch (error) {
      //       console.error(
      //         "Error updating loggedinUserInfo with auth methods:",
      //         error
      //       );
      //     }
      //   }
      // }

      setShowModal(false);
      setIsLoading(false);
      setIsEditMode(false);
      // Refresh the data to show updated values
      // dispatch(getAllAppSetups());
      window.location.reload()
    } catch (error) {
      console.error("Failed to save:", error);

      // Extract error message from various possible locations
      // When using .unwrap(), the error is the value passed to rejectWithValue()
      // which could be a string (message) or an object
      let errorMessage = "Unknown error";

      // Check if error is a string (from rejectWithValue passing message directly)
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object") {
        // Check for {success: false, message: "Incorrect password"} structure
        if (error.success === false && error.message) {
          errorMessage = error.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        }
      }

      // Fallback to checking nested structures
      if (errorMessage === "Unknown error") {
        errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          error?.payload?.message ||
          error?.payload?.error ||
          (error?.payload &&
            typeof error.payload === "object" &&
            error.payload.message) ||
          (error?.payload &&
            typeof error.payload === "object" &&
            error.payload.error) ||
          "Unknown error";
      }

      // For "Incorrect password", show the message directly
      if (errorMessage === "Incorrect password") {
        showSnackbar("Incorrect password", "error");
      } else {
        showSnackbar(
          `Error saving application setup: ${errorMessage}`,
          "error"
        );
      }

      setIsLoading(false);
      // Don't close the modal on error so user can retry
    }
  };

  const toggleMethod = (method) => {
    setSignatureMethods((prev) =>
      prev.includes(method)
        ? prev.filter((m) => m !== method)
        : [...prev, method]
    );
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleBackClick = () => {
    if (isEditMode) {
      // Check for unsaved changes
      const latestCompanyName = lastItem?.company_name || "";
      const latestCompanyAddress = lastItem?.company_address || "";
      const latestCompanyPhone = lastItem?.company_phone || "";
      const latestFooter = lastItem?.footer || "";
      const latestMethods = Array.isArray(lastItem?.signature_auth_methods)
        ? lastItem.signature_auth_methods
        : [];
      const latestAutoBackup =
        lastItem?.is_autobackup_enabled || lastItem?.auto_backup_enabled || false;
      const latestBackupTime =
        lastItem?.auto_backup_time || lastItem?.backup_time || "10:10 AM";
      const latestTimezone =
        lastItem?.application_time_zone || lastItem?.timezone || "EST";
      
      // Parse latest backup time for accurate comparison
      const parseBackupTime = (timeString) => {
        if (!timeString) return { hour: "10", minute: "10", period: "AM" };
        const timeMatch = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (timeMatch) {
          // parseBackupTime in populateForm converts to 2-digit strings for state
          // We need to match that logic to compare correctly with state
          return {
            hour: timeMatch[1].padStart(2, "0"),
            minute: timeMatch[2],
            period: timeMatch[3].toUpperCase(),
          };
        }
        // Try 24-hour format
        const time24Match = timeString.match(/(\d{1,2}):(\d{2})/);
        if (time24Match) {
          const hour24 = parseInt(time24Match[1], 10);
          const hour12 = hour24 % 12 || 12;
          const period = hour24 >= 12 ? "PM" : "AM";
          return {
            hour: hour12.toString().padStart(2, "0"),
            minute: time24Match[2],
            period: period,
          };
        }
        return { hour: "10", minute: "10", period: "AM" };
      };

      const latestTimeParsed = parseBackupTime(latestBackupTime);
      const isTimeChanged = 
        backupHour !== latestTimeParsed.hour || 
        backupMinute !== latestTimeParsed.minute || 
        backupPeriod !== latestTimeParsed.period;

      const hasChanges = 
        companyName !== latestCompanyName ||
        companyAddress !== latestCompanyAddress ||
        companyPhone !== latestCompanyPhone ||
        footerText !== latestFooter ||
        autoBackupEnabled !== latestAutoBackup ||
        timezone !== latestTimezone ||
        // Fix: Sort copies of arrays to avoid mutating state/props
        JSON.stringify([...signatureMethods].sort()) !== JSON.stringify([...latestMethods].sort()) ||
        // Only check backup time if auto backup is enabled
        (autoBackupEnabled && isTimeChanged) ||
        // Check if image changed (file selected or base64 string changed)
        !!companyImageFile || (!!companyImageBase64 && !lastItem?.company_image?.includes(companyImageBase64));

      if (hasChanges) {
        const confirmLeave = window.confirm("You have unsaved changes. Are you sure you want to leave?");
        if (!confirmLeave) {
          return;
        }
      }

      // Exit edit mode instead of navigating
      setIsEditMode(false);
      populateForm(lastItem);
    } else {
      // Navigate back when in view mode
      navigate(-1);
    }
  };

  return (
    <div className="app-setup-page">
      <AppBar
        title={title}
        buttons={
          isEditMode
            ? [
                {
                  label: "Save",
                  onClick: canEdit ? handleSave : undefined,
                  disabled: !canEdit,
                  title: !canEdit
                    ? "You do not have permission to save application setup."
                    : undefined,
                },
              ]
            : []
        }
        editButton={
          !isEditMode && canEdit ? (
            <Button onClick={handleEditClick} size="sm">
              Edit
            </Button>
          ) : null
        }
        onBack={handleBackClick}
        showTablesDropdown={false}
        showProfileButton={!isEditMode}
      />
      <div className="app-setup-container">
        <div className="signature-auth-wrapper">
          <SignatureAuthSection
            signatureMethods={signatureMethods}
            onToggleMethod={toggleMethod}
            disabled={!isEditMode}
          />
          <div className="timezone-section">
            {isEditMode ? (
              <>
                <label className="timezone-label">Timezone</label>
                <CustomSelect
                  value={timezone}
                  onChange={(value) => setTimezone(value)}
                  options={[
                    { value: "EST", label: "EST" },
                    { value: "PST", label: "PST" },
                    { value: "UTC", label: "UTC" },
                    { value: "EDT", label: "EDT" },
                  ]}
                  disabled={!isEditMode}
                  style={{ width: "150px" }}
                />
              </>
            ) : (
              <div className="timezone-view-mode">Timezone: {timezone}</div>
            )}
          </div>
        </div>

        <div className="form-grid" style={{ marginTop: "-30px" }}>
          <TextField
            label="Company Name"
            required={isEditMode}
            style={{ marginLeft: "-2px" }}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            disabled={!isEditMode}
          />

          <TextField
            label="Company Address"
            required={isEditMode}
            value={companyAddress}
            onChange={(e) => setCompanyAddress(e.target.value)}
            disabled={!isEditMode}
          />

          <TextField
            ref={phoneInputRef}
            label="Company Phone"
            required={isEditMode}
            value={companyPhone}
            onChange={handlePhoneChange}
            onKeyPress={handlePhoneKeyPress}
            onKeyDown={handlePhoneKeyDown}
            type="tel"
            placeholder="XXX-XXX-XXXX"
            inputMode="numeric"
            pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
            maxLength={13}
            disabled={!isEditMode}
          />

          <LogoUpload
            previewUrl={previewUrl}
            onFileSelect={handleLogoUpload}
            disabled={!isEditMode}
          />
        </div>

        <div className="footer-row">
          <TextArea
            label="Footer"
            required={isEditMode}
            value={footerText}
            style={{ marginLeft: "-2px" }}
            textareaStyle={{
              width: "673px",
              height: "100px",
              borderRadius: "4px",
            }}
            onChange={(e) => setFooterText(e.target.value)}
            placeholder="Enter footer text..."
            minHeight="100px"
            disabled={!isEditMode}
          />
        </div>

        <div className="auto-backup-section">
          <div className="auto-backup-heading">
            Auto Backup
          </div>
          <div className="auto-backup-row">
            <CheckboxField
              checked={autoBackupEnabled}
              onChange={(e) => setAutoBackupEnabled(e.target.checked)}
              description="Enable Auto Backup"
              disabled={!isEditMode}
            />
          </div>
          {autoBackupEnabled && (
            <div className="backup-time-container">
              <label className="backup-time-label">Set Time</label>
              <div className="backup-time-inputs-wrapper">
                <input
                  type="number"
                  className="backup-hour-input"
                  value={backupHour}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (parseInt(value, 10) >= 1 && parseInt(value, 10) <= 12)
                    ) {
                      setBackupHour(value);
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === "" || parseInt(value, 10) < 1) {
                      setBackupHour("01");
                    } else if (parseInt(value, 10) > 12) {
                      setBackupHour("12");
                    } else {
                      setBackupHour(value.padStart(2, "0"));
                    }
                  }}
                  min="1"
                  max="12"
                  placeholder="HH"
                  disabled={!isEditMode}
                />
                <span className="backup-time-separator">:</span>
                <input
                  type="number"
                  className="backup-minute-input"
                  value={backupMinute}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (parseInt(value, 10) >= 0 && parseInt(value, 10) <= 59)
                    ) {
                      setBackupMinute(value);
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === "" || parseInt(value, 10) < 0) {
                      setBackupMinute("00");
                    } else if (parseInt(value, 10) > 59) {
                      setBackupMinute("59");
                    } else {
                      setBackupMinute(value.padStart(2, "0"));
                    }
                  }}
                  min="0"
                  max="59"
                  placeholder="MM"
                  disabled={!isEditMode}
                />
                <CustomSelect
                  value={backupPeriod}
                  onChange={(value) => setBackupPeriod(value)}
                  options={[
                    { value: "AM", label: "AM" },
                    { value: "PM", label: "PM" },
                  ]}
                  disabled={!isEditMode}
                  audittrail={false}
                  style={{ width: "80px" }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <SaveModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleConfirmSave}
        saveComment={saveComment}
        setSaveComment={setSaveComment}
        savePassword={savePassword}
        setSavePassword={setSavePassword}
        isLoading={isLoading}
        showLoader={true}
      />

      {isLoading && (
        <Loader
          text={
            isEditing
              ? "Saving application setup..."
              : "Creating application setup..."
          }
        />
      )}

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </div>
  );
};

export default ApplicationSetup;
